import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { supabase } from "@/lib/supabase";

type OrderItem = {
  id: number;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  final_price: number | null;
  subtotal: number | null;
};

type Order = {
  id: number;
  status: string;
  currency: string;
  subtotal_items: number | null;
  tax_total: number | null;
  shipping_total: number | null;
  discount_price: number | null;
  recipient: any;
  customer_notes: string | null;
  created_at: string;
  order_items: OrderItem[];
};

export default function SiteOrderConfirmationPage() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    (async () => {
      const { data } = await supabase
        .from("order_headers")
        .select("*, order_items(*)")
        .eq("id", Number(id))
        .maybeSingle();
      if (mounted) {
        setOrder(data as Order | null);
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <SiteLayout navVariant="solid">
        <div className="min-h-[60vh] pt-32 flex items-center justify-center text-ink-500 text-sm">
          <i className="ri-loader-4-line animate-spin text-2xl text-brand-500 mr-2"></i>
          Loading order…
        </div>
      </SiteLayout>
    );
  }

  if (!order) {
    return (
      <SiteLayout navVariant="solid">
        <div className="min-h-[60vh] pt-32 flex flex-col items-center justify-center gap-4 px-4">
          <i className="ri-error-warning-line text-5xl text-ink-300"></i>
          <h1 className="font-display text-2xl font-bold text-ink-900">Order not found</h1>
          <Link
            to="/site/services"
            className="px-5 py-2.5 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer"
          >
            Back to all services
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const subtotal = Number(order.subtotal_items ?? 0);
  const tax = Number(order.tax_total ?? 0);
  const shipping = Number(order.shipping_total ?? 0);
  const discount = Number(order.discount_price ?? 0);
  const total = subtotal + tax + shipping - discount;
  const r = order.recipient ?? {};

  return (
    <SiteLayout navVariant="solid">
      <div className="pt-24 md:pt-28 pb-16 md:pb-24 bg-cream-50 min-h-screen">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Success header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <i className="ri-check-line text-4xl"></i>
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 mb-3">
              Booking confirmed!
            </h1>
            <p className="text-ink-600 max-w-md mx-auto">
              Thanks {r.name?.split(" ")[0] || "there"} — we've got your move on the books. A real human will call you within 1 business hour to confirm.
            </p>
            <div className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-white border border-ink-200 text-xs font-semibold text-ink-700">
              <span className="text-ink-400">Order</span>
              <span className="font-mono text-ink-900">#{order.id}</span>
              <span className="text-ink-300">·</span>
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] uppercase tracking-wider font-bold">
                {order.status.replace(/_/g, " ")}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-ink-100 overflow-hidden">
            {/* Items */}
            <div className="p-6 md:p-7 border-b border-ink-100">
              <h3 className="font-display text-base font-bold text-ink-900 mb-4">Service</h3>
              {order.order_items?.map((it) => (
                <div key={it.id} className="flex items-center justify-between py-2">
                  <div>
                    <div className="text-sm font-semibold text-ink-900">{it.product_name}</div>
                    <div className="text-xs text-ink-500 mt-0.5">Qty {it.quantity}</div>
                  </div>
                  <div className="text-sm font-semibold text-ink-900">
                    ${Number(it.subtotal ?? it.final_price ?? it.unit_price).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>

            {/* Pricing */}
            <div className="p-6 md:p-7 border-b border-ink-100 space-y-2">
              <Row label="Subtotal" value={`$${subtotal.toLocaleString()}`} />
              {tax > 0 && <Row label="Tax" value={`$${tax.toLocaleString()}`} />}
              {discount > 0 && <Row label="Discount" value={`-$${discount.toLocaleString()}`} />}
              <div className="pt-3 mt-2 border-t border-ink-100 flex items-center justify-between">
                <div className="text-sm font-bold text-ink-900">Total</div>
                <div className="font-display text-xl font-bold text-ink-900">
                  ${total.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Move info */}
            <div className="p-6 md:p-7 border-b border-ink-100 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InfoBlock icon="ri-user-line" label="Contact">
                <div className="text-sm font-semibold text-ink-900">{r.name}</div>
                <div className="text-xs text-ink-500">{r.email}</div>
                <div className="text-xs text-ink-500">{r.phone}</div>
              </InfoBlock>
              <InfoBlock icon="ri-calendar-line" label="Move date">
                <div className="text-sm font-semibold text-ink-900">
                  {r.move_date
                    ? new Date(r.move_date + "T00:00:00").toLocaleDateString(undefined, {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                      })
                    : "—"}
                </div>
                <div className="text-xs text-ink-500">Start: {r.move_time || "TBD"}</div>
              </InfoBlock>
              <InfoBlock icon="ri-map-pin-line" label="Pickup">
                <div className="text-sm text-ink-900">{r.address}</div>
                <div className="text-xs text-ink-500">
                  {[r.city, r.state, r.zip].filter(Boolean).join(", ")}
                </div>
              </InfoBlock>
              <InfoBlock icon="ri-flag-line" label="Drop-off">
                <div className="text-sm text-ink-900">{r.dropoff_address}</div>
                <div className="text-xs text-ink-500">
                  {[r.dropoff_city, r.dropoff_state, r.dropoff_zip].filter(Boolean).join(", ")}
                </div>
              </InfoBlock>
            </div>

            {/* Notes */}
            {order.customer_notes && (
              <div className="p-6 md:p-7 border-b border-ink-100">
                <div className="text-xs uppercase tracking-wider font-bold text-ink-400 mb-2">Notes for the crew</div>
                <p className="text-sm text-ink-700 leading-relaxed">{order.customer_notes}</p>
              </div>
            )}

            {/* Next steps */}
            <div className="p-6 md:p-7 bg-cream-50">
              <h4 className="font-display text-sm font-bold text-ink-900 mb-3">What happens next</h4>
              <ol className="space-y-2.5 text-sm text-ink-700">
                <Step n={1}>We'll call within 1 business hour to confirm details and answer questions.</Step>
                <Step n={2}>A 20% deposit locks in your date — we'll send a payment link by email.</Step>
                <Step n={3}>Your crew gets dispatched the morning of your move. GPS tracking link included.</Step>
              </ol>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/site/services"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold border border-ink-200 hover:border-ink-300 hover:bg-white text-ink-800 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Browse more services
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap transition-colors"
            >
              Back to home
            </Link>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-ink-600">{label}</span>
      <span className="font-semibold text-ink-900">{value}</span>
    </div>
  );
}

function InfoBlock({ icon, label, children }: { icon: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-ink-400 mb-2">
        <i className={icon}></i>
        {label}
      </div>
      {children}
    </div>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-brand-500 text-white text-[10px] font-bold mt-0.5">
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}